import BaseAddress from '../functions/baseAddress';
import {
  Column,
  ColumnValue,
  Policy,
  ChangeLogItem,
  CurrentContext,
  BasePolicySettings,
  Project,
  SideMenu,
  SSOProvider,
  ProjectListItem,
} from '~/models';
import { TableData } from '~/_base/components/adminTable/adminTable';
import ApiCall, { fetchResponseOnly } from '~/_base/functions/apicalls';
import { RailsMenu, RailsOperation } from '~/_base/hooks/useRailsClick';
import { Company } from '~/_base/components/companyList';
import { NewsfeedMessage } from '~/frontend/modules/projectDashboard/projectDashboard';
import { ListEntity } from '~/frontend/modules/listItemTable';
import transformWbsData from '~/_base/functions/transformWbsData';

const host = BaseAddress();

const api = {
  newsfeed: {
    address: (id: number) => `${host}/projects/${id}/newsfeed?per_page=100`,
    request(id: number) {
      return ApiCall<NewsfeedMessage[]>(api.newsfeed.address(id));
    },
  },
  userData: {
    address: (id: string, type: string) =>
      `${host}/users/${id}/show_admin.json?type=${type}`,
    request(id: string, type: string) {
      return ApiCall(api.userData.address(id, type));
    },
  },
  createUser: {
    address: `${host}/users/`,
    request(payload: string) {
      return ApiCall(api.createUser.address, 'POST', null, payload);
    },
  },
  updateUser: {
    address: (id: string) => `${host}/users/${id}`,
    request(id: string, payload: string) {
      return ApiCall(api.updateUser.address(id), 'PATCH', null, payload);
    },
  },
  getActionType: {
    address: (actionId: string) => `${host}/actions/${actionId}/action_type`,
    request(actionId: string) {
      return ApiCall<{ action_type: string | null }>(
        api.getActionType.address(actionId)
      );
    },
  },
  topNavigation: {
    address: `${host}/navigation`,
    request() {
      return ApiCall<(RailsMenu | RailsOperation)[]>(api.topNavigation.address);
    },
  },
  projectNavigation: {
    address: (id: string) => `${host}/projects/${id}/navigation`,
    request(url: string) {
      return ApiCall<RailsMenu[]>(url);
    },
  },
  sidemenu: {
    address: `${host}/sidemenu_data`,
    request(url: string) {
      return ApiCall<SideMenu>(url);
    },
  },
  currentContext: {
    address: `${host}/sessions/current_context.json`,
    request() {
      return ApiCall<CurrentContext>(api.currentContext.address);
    },
  },
  projects: {
    address: `${host}/projects.json`,
    request(url: string) {
      return ApiCall<Project[]>(url);
    },
  },
  companies: {
    address: `${host}/companies.json`,
    request() {
      return ApiCall<Company[]>(api.companies.address);
    },
  },
  updateCompany: {
    address: (id: string) => `${host}/companies/${id}`,
    request(id: string, payload: string) {
      return ApiCall(api.updateCompany.address(id), 'PUT', null, payload);
    },
  },
  createCompany: {
    address: `${host}/companies`,
    request(payload: string) {
      return ApiCall(api.createCompany.address, 'POST', null, payload);
    },
  },
  orgLists: {
    address: `${host}/organisation_lists.json`,
    request() {
      return ApiCall<Omit<OrgListResponse, 'column_definitions'>[]>(
        api.orgLists.address
      );
    },
  },
  orgListById: {
    address: (id: string) => `${host}/organisation_lists/${id}.json`,
    request(id: string) {
      return ApiCall<OrgListResponse>(api.orgListById.address(id));
    },
  },
  orgListByName: {
    address: (name: string) =>
      `${host}/organisation_lists.json?name=${encodeURIComponent(name)}`,
  },
  orgListData: {
    address: (id: string) => `${host}/organisation_lists/${id}/show_admin.json`,
    request(id: string) {
      return ApiCall<EntityDataResponse<ListEntity, BasePolicySettings, {}>>(
        api.orgListData.address(id)
      );
    },
  },
  orgListItem: {
    address: (listId: string, itemId: string) =>
      `${host}/organisation_lists/${listId}/items/${itemId}.json`,
    request(listId: string, itemId: string) {
      return ApiCall<ColumnValue[]>(api.orgListItem.address(listId, itemId));
    },
  },
  orgListWithItems: {
    address: (id: string) => `${host}/organisation_lists/${id}.json?items=true`,
    request(id: string) {
      return ApiCall<OrgListResponseWithItems>(
        api.orgListWithItems.address(id)
      );
    },
  },
  updateOrgListItem: {
    address: (id: string) =>
      `${host}/organisation_lists/${id}/update_list_items.json`,
    request(id: string, payload: string) {
      return ApiCall(api.updateOrgListItem.address(id), 'PATCH', null, payload);
    },
  },
  projectMembers: {
    address: (project_id: string, draft_template_id: string) =>
      `${host}/projects/${project_id}/project_members.json?draft_template_id=${draft_template_id}`,
    request(project_id: string, draft_template_id: string) {
      return ApiCall<ProjectMemberWithUser[]>(
        api.projectMembers.address(project_id, draft_template_id)
      );
    },
  },
  projectLists: {
    address: `${host}/standard_project_lists.json`,
    request() {
      return ApiCall<ProjectList[]>(api.projectLists.address);
    },
  },
  signin: {
    address: `${host}/sessions`,
    request(payload: string) {
      return ApiCall<{ location?: string }>(
        api.signin.address,
        'POST',
        null,
        payload
      );
    },
  },
  signout: {
    address: `${host}/signout`,
    request() {
      return ApiCall(api.signout.address, 'DELETE');
    },
  },
  ssoProviders: {
    address: `${host}/signin.json`,
    request() {
      return ApiCall<SSOProvidersResponse>(api.ssoProviders.address);
    },
  },
  serversByEmail: {
    address: `${host}/server/lookup.json`,
    request(payload: string) {
      return ApiCall<{ matches: number }>(
        api.serversByEmail.address,
        'POST',
        null,
        payload
      );
    },
  },
  formExportOperations: {
    address: `${host}/forms/available_export_operations`,
    request() {
      return ApiCall<RailsOperation[]>(api.formExportOperations.address);
    },
  },
  memosExportOperations: {
    address: `${host}/memos/available_export_operations`,
    request() {
      return ApiCall<RailsOperation[]>(api.memosExportOperations.address);
    },
  },
  actionsExportOperations: {
    address: `${host}/actions/available_export_operations`,
    request() {
      return ApiCall<RailsOperation[]>(api.actionsExportOperations.address);
    },
  },
  drawingsUploadOptions: {
    address: `${host}/drawings/available_upload_operations`,
    request() {
      return ApiCall<RailsOperation[]>(api.drawingsUploadOptions.address);
    },
  },
  punchlistOptions: {
    address: `${host}/actions/available_punch_list_operations`,
    request() {
      return ApiCall<RailsOperation[]>(api.punchlistOptions.address);
    },
  },
  approvalTotals: {
    address: `${host}/forms/approvals_totals.json`,
    request() {
      return ApiCall<{ to_approve: number; rejected: number }>(
        api.approvalTotals.address
      );
    },
  },
  formPermissions: {
    address: (id: string) => `${host}/base_forms/${id}/permissions_when_linked`,
    request(id: string) {
      return ApiCall<{ can_view: boolean; can_edit: boolean }>(
        api.formPermissions.address(id)
      );
    },
  },
  templatePermissions: {
    address: (id: string, project_id: string) =>
      `${host}/templates/${id}/permissions_when_linked?project_id=${project_id}`,
    request(id: string, project_id: string) {
      return ApiCall<{ can_create: boolean; can_attach: boolean }>(
        api.templatePermissions.address(id, project_id)
      );
    },
  },
  unknownUserMessage: {
    address: (id: string) =>
      `${host}/unknown_user.json?identity_provider_id=${id}`,
    request(id: string) {
      return ApiCall<{ unknown_user_message: string }>(
        api.unknownUserMessage.address(id)
      );
    },
  },
  availableTemplates: {
    address: (id: string, registerType: string) =>
      `${host}/projects/${id}/available_templates.json?register=${registerType}`,
    request(id: string, registerType: string) {
      return ApiCall<RailsOperation[]>(
        api.availableTemplates.address(id, registerType)
      );
    },
  },
  registerItemActions: {
    address: (registerType: string, formId: string) =>
      `${host}/${registerType}/${formId}/available_operations`,
    request(registerType: string, formId: string) {
      return ApiCall<(RailsOperation | RailsMenu)[]>(
        api.registerItemActions.address(registerType, formId)
      );
    },
  },
  getProjectMember: {
    address: (projectId: string, projectMemberId: string) =>
      `${host}/projects/${projectId}/project_members/${projectMemberId}.json`,
    request(projectId: string, projectMemberId: string) {
      return ApiCall<ProjectMemberWithUser>(
        api.getProjectMember.address(projectId, projectMemberId)
      );
    },
  },
  projectMembersList: {
    address: (id: string) => `${host}/projects/${id}/project_members/list.json`,
    request(id: string) {
      return ApiCall<TableData<ProjectMember>>(
        api.projectMembersList.address(id)
      );
    },
  },
  projectMembersTableActions: {
    address: (id: string) =>
      `${host}/projects/${id}/project_members/index_operations.json`,
    request(id: string) {
      return ApiCall<(RailsMenu | RailsOperation)[]>(
        api.projectMembersTableActions.address(id)
      );
    },
  },
  updateProjectMember: {
    address: (projectId: string, projectMemberId: string) =>
      `/projects/${projectId}/project_members/${projectMemberId}`,
    request(projectId: string, projectMemberId: string, payload: string) {
      return ApiCall(
        api.updateProjectMember.address(projectId, projectMemberId),
        'PUT',
        null,
        payload
      );
    },
  },
  createProjectMember: {
    address: (projectId: string) => `/projects/${projectId}/project_members`,
    request(projectId: string, payload: string) {
      return ApiCall(
        api.createProjectMember.address(projectId),
        'POST',
        null,
        payload
      );
    },
  },
  projectMemberFormOptions: {
    address: (projectId: string) =>
      `/projects/${projectId}/project_members/form_options.json`,
    request(projectId: string) {
      return ApiCall<{
        users_not_in_project: UserNotInProject[];
      }>(api.projectMemberFormOptions.address(projectId));
    },
  },
  getApproverTypes: {
    address: () => `/approver_types.json`,
    request() {
      return ApiCall<ApproverType[]>(api.getApproverTypes.address());
    },
  },
  getApproverTypesByUserId: {
    address: (userId: string) => `${host}/users/${userId}/approver_types.json`,
    request(userId: string) {
      return ApiCall<ApproverType[]>(
        api.getApproverTypesByUserId.address(userId)
      );
    },
  },
  getApproversByProject: {
    address: (projectId: string) => `${host}/projects/${projectId}/approvers`,
    request(projectId: string) {
      return ApiCall<ApproverWithApproverTypes[]>(
        api.getApproversByProject.address(projectId)
      );
    },
  },
  projectListItems: {
    address: (projectId: string, projectListId: string) =>
      `${host}/projects/${projectId}/project_lists/${projectListId}.json?find_by_list_type=true`,
    request: (projectId: string, projectListId: string) => {
      return ApiCall<ProjectListResponse>(
        api.projectListItems.address(projectId, projectListId)
      );
    },
  },

  standardProjects: {
    address: () => `${host}/projects/standard_projects`,
    async request() {
      const projects = await ApiCall<StandardProject[]>(
        api.standardProjects.address()
      );
      const collator = new Intl.Collator(undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      projects.sort((a, b) => {
        let A = a['job_number'];
        let B = b['job_number'];
        return collator.compare(A, B);
      });
      return projects;
    },
  },
  wbsItems: {
    address: (projectId: string) =>
      `${host}/projects/${projectId}/wbs_items.json`,
    async request(projectId: string, filter?: (item: BaseWbsItem) => boolean) {
      const wbsItems = await ApiCall<BaseWbsItem[]>(
        api.wbsItems.address(projectId)
      );
      return transformWbsData(filter ? wbsItems.filter(filter) : wbsItems);
    },
  },
  wbsItemDetails: {
    address: (projectId: string, itemId: string) =>
      `${host}/projects/${projectId}/wbs_items/${itemId}.json`,
    request(projectId: string, itemId: string) {
      return ApiCall<BaseWbsItem>(
        api.wbsItemDetails.address(projectId, itemId)
      );
    },
  },
  startProjectSync: {
    address: (projectId: string) => `${host}/projects/${projectId}/start_sync`,
    request(projectId: string) {
      return ApiCall(api.startProjectSync.address(projectId));
    },
  },
  domainCheck: {
    address: (value: string) =>
      `${host}/trialsetup/available_subdomains/${value}`,
    request(value: string) {
      return ApiCall(api.domainCheck.address(value), 'GET');
    },
  },
  badEmailDomains: {
    address: () => `${host}/bad_email_domains.txt`,
    async request() {
      const res = await fetchResponseOnly(api.badEmailDomains.address(), 'GET');
      const parsed = await res.text();
      return parsed.split('\n');
    },
  },
  submitTrialSetup: {
    address: () => `${host}/api/v1/customers`,
    request(submission: string) {
      return fetchResponseOnly(
        api.submitTrialSetup.address(),
        'POST',
        null,
        submission
      );
    },
  },
  formApprovalInfo: {
    address: (id: string) => `${host}/forms/${id}/approval_info`,
    request(id: string) {
      return ApiCall<FormApprovalInfo>(api.formApprovalInfo.address(id));
    },
  },
  renewSession: {
    address: `${host}/refresh_session_state`,
    request() {
      return ApiCall(api.renewSession.address);
    },
  },
  checkSession: {
    address: `${host}/current_user_session`,
    request() {
      return fetchResponseOnly(api.checkSession.address);
    },
  },
  customFilterLabels: {
    address: `${host}/custom_filter_labels`,
    request() {
      return ApiCall<CustomFilterLabel[]>(api.customFilterLabels.address);
    },
  },
  customFilterValuesInUse: {
    address: (id: number, archiveMode: boolean) =>
      `${host}/projects/${id}/custom_filter_values_in_use?archived=${archiveMode}`,
    request(id: number, archiveMode: boolean) {
      return ApiCall<{ label_id: number; label: string; value: string }[]>(
        api.customFilterValuesInUse.address(id, archiveMode)
      );
    },
  },
};

// custom_filter_labels
export interface CustomFilterLabel {
  id: number;
  uuid: string;
  active: boolean;
  name: string;
}

// forms/:id/approval_info
export interface FormApprovalInfo {
  all_sections_approved: boolean;
  managed_by_damstra: boolean;
}

// projects/:project_id/wbs_items.json
export interface BaseWbsItem {
  id: number;
  project_id: number;
  number: string;
  name: string;
  order: string;
}

export interface StandardProject {
  id: number;
  job_number: string;
  name: string;
  sync_state: 'na' | 'synced' | 'not_synced'; // Android only
}

export interface UserNotInProject {
  name: string;
  id: number;
  show_approver_types: boolean;
  type: string;
}

// projects/:id/project_lists/:id.json?find_by_list_type=true
export interface ProjectListResponse {
  id: number;
  project_id: number;
  standard_project_list_id: number;
  lock_version: number;
  created_at: string;
  updated_at: string;
  name: string;
  project_list_items: ProjectListItem[];
  standard_project_list: { managed: boolean };
}

// /projects/:id/approvers
export interface ApproverWithApproverTypes {
  id: number;
  name: string;
  approver_types: { id: number }[];
  email: string;
  damstra_id: string;
}

export interface ApproverType {
  id: number;
  active: boolean;
  name: string;
}

// projects/:project_id/project_members.json
export interface ProjectMemberWithUser extends ProjectMember {
  user: {
    name: string;
    id: number;
    user_type: string;
    email: string;
    damstra_id: string;
    job_title?: string;
    department?: string;
  };
}

// projects/:id/project_members/:id.json
export interface ProjectMember {
  id: number;
  project_id: number;
  user_id: number;
  permission_level: number;
  can_approve: boolean;
  approver_type_names: string[];
  approver_type_ids: number[];
  show_approver_types: boolean;
}

// signin.json
export interface SSOProvidersResponse {
  app: string;
  providers: SSOProvider[];
}

// {entity}/show_admin.json
// Where entity can be projects, users, lists, etc.
export interface EntityDataResponse<Structure, Settings, Attributes> {
  entity: Structure;
  policy: Policy<Settings, Attributes>;
  change_log: ChangeLogItem[];
}

// organisation_lists/:id
export interface OrgListResponse {
  id: number;
  list_version: number;
  lock_version: number;
  locked: boolean;
  name: string;
  subtitle_fields: string[];
  title_fields: string[];
  column_definitions: Column[];
}

// organisation_lists/:id?items=true
export interface OrgListResponseWithItems extends OrgListResponse {
  items?: {
    id: number;
    uuid: string;
    name: string;
    values: ColumnValue[];
  }[];
  item_values_parsed?: string[];
}

// standard_project_lists.json
export interface ProjectList {
  id: number;
  name: string;
}

export default api;
